import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AdminLogin from "@/pages/AdminLogin";
import { ThemeProvider } from "@/context/ThemeContext";
import "@/styles/dark.css";
import AuthCallback from "@/pages/AuthCallback";
import PendingAccess from "@/pages/PendingAccess";
import ChangePassword from "@/pages/ChangePassword";
import Dashboard from "@/pages/Dashboard";
import ModulePage from "@/pages/ModulePage";
import Settings from "@/pages/Settings";
import Trends from "@/pages/Trends";
import Automation from "@/pages/Automation";
import Templates from "@/pages/Templates";
import ItemMaster from "@/pages/store/ItemMaster";
import Transactions from "@/pages/store/Transactions";
import IndentPlanner from "@/pages/store/IndentPlanner";
import InventoryOverview from "@/pages/store/InventoryOverview";
import Personnel from "@/pages/Personnel";
import PatientDirectory from "@/pages/PatientDirectory";
import Manual from "@/pages/Manual";
import Abdm from "@/pages/Abdm";
import { ModuleGate } from "@/components/ModuleGate";
import AdminPanel from "@/pages/AdminPanel";
import LabOverview from "@/pages/LabOverview";
import { Toaster } from "@/components/ui/sonner";

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#FDFDFD]">
    <p className="text-sm text-[#6B7280]">Loading LabTrack…</p>
  </div>
);

function Protected({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if ((user.status || "active") !== "active") return <PendingAccess />;
  if (user.must_change_password) return <ChangePassword />;
  if (adminOnly && user.role !== "admin") {
    return (
      <div className="rounded-md border border-[#E5E7EB] bg-white p-8 text-center" data-testid="access-denied">
        <p className="font-heading font-bold text-[#111827]">Admin access required</p>
        <p className="text-sm text-[#6B7280] mt-1">Ask a lab administrator for access to this page.</p>
      </div>
    );
  }
  return children;
}

function AppRoutes() {
  const location = useLocation();
  const { user, loading } = useAuth();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={loading ? <Loading /> : user ? <Navigate to="/dashboard" replace /> : <Landing />} />
      <Route path="/login" element={loading ? <Loading /> : user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/admin-login" element={loading ? <Loading /> : user ? <Navigate to="/dashboard" replace /> : <AdminLogin />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/m/:module" element={<ModulePage />} />
        <Route path="/trends" element={<Trends />} />
        <Route path="/templates" element={<Templates />} />
        <Route path="/store" element={<ModuleGate module="inventory" label="the sub-store"><InventoryOverview /></ModuleGate>} />
        <Route path="/store/items" element={<ModuleGate module="inventory" label="the item master"><ItemMaster /></ModuleGate>} />
        <Route path="/store/transactions" element={<ModuleGate module="inventory" label="stock transactions"><Transactions /></ModuleGate>} />
        <Route path="/store/indents" element={<ModuleGate module="indents" label="the indent planner"><IndentPlanner /></ModuleGate>} />
        <Route path="/personnel" element={<Personnel />} />
        <Route path="/patients" element={<ModuleGate module="samples" label="the patient directory"><PatientDirectory /></ModuleGate>} />
        <Route path="/manual" element={<Manual />} />
        <Route path="/abdm" element={<Abdm />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/automation" element={<Protected adminOnly><Automation /></Protected>} />
        <Route path="/admin" element={<Protected adminOnly><AdminPanel /></Protected>} />
        <Route path="/labs" element={<Protected adminOnly><LabOverview /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to={user ? "/dashboard" : "/"} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="bottom-right" />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
